import { StyleSheet } from 'aphrodite';
import {
	brandPrimaryText,
	header,
	midGray,
	background,
	borderOutline,
	darkGrayFontColor,
	titles,
	warningLight,
	warningDark,
	errorLight,
	destructive,
	white,
} from '../../../web/styles/colors';

export const styleSheet = StyleSheet.create({
	container: {
		padding: '24px',
		maxWidth: '1000px',
		margin: '0 auto',
	},
	section: {
		marginBottom: '32px',
	},
	sectionTitle: {
		color: header,
		fontSize: '18px',
		fontWeight: 600,
		marginBottom: '16px',
	},
	formGroup: {
		marginBottom: '24px',
	},
	label: {
		color: header,
		fontSize: '14px',
		fontWeight: 600,
		marginBottom: '8px',
		display: 'block',
	},
	dateNavigationContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '16px',
		marginBottom: '8px',
	},
	dateNavButton: {
		padding: '8px 16px',
		fontSize: '20px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		backgroundColor: 'white',
		color: header,
		minWidth: '48px',
		':hover:not(:disabled)': {
			backgroundColor: '#f5f5f5',
		},
		':disabled': {
			opacity: 0.3,
			cursor: 'not-allowed',
		},
	},
	dateDisplay: {
		fontSize: '16px',
		fontWeight: 600,
		color: header,
		flex: 1,
		textAlign: 'center',
	},
	dateDisplayClickable: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: brandPrimaryText,
		textDecoration: 'underline',
		':hover:not(:disabled)': {
			opacity: 0.8,
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
			color: header,
			textDecoration: 'none',
		},
	},
	todayIndicator: {
		fontSize: '12px',
		color: brandPrimaryText,
		fontWeight: 600,
		marginTop: '4px',
	},
	button: {
		padding: '10px 24px',
		fontSize: '14px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: 'none',
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	primaryButton: {
		backgroundColor: brandPrimaryText,
		color: 'white',
		':hover': {
			opacity: 0.9,
		},
	},
	secondaryButton: {
		backgroundColor: midGray,
		color: header,
		':hover': {
			opacity: 0.9,
		},
	},
	meetingsTable: {
		width: '100%',
		borderCollapse: 'collapse',
		marginTop: '16px',
		border: `1px solid ${midGray}`,
	},
	tableHeader: {
		backgroundColor: '#f5f5f5',
		borderBottom: `2px solid ${midGray}`,
	},
	tableHeaderCell: {
		padding: '12px',
		textAlign: 'left',
		fontWeight: 600,
		fontSize: '14px',
		color: header,
	},
	meetingDetailsHeader: {
		maxWidth: '400px',
	},
	tableRow: {
		borderBottom: `1px solid ${midGray}`,
		':hover': {
			backgroundColor: '#f9f9f9',
		},
	},
	tableCell: {
		padding: '12px',
		fontSize: '14px',
	},
	meetingDetailsCell: {
		maxWidth: '400px',
		wordWrap: 'break-word',
	},
	meetingTitle: {
		fontWeight: 600,
		marginBottom: '4px',
	},
	attendeesPreview: {
		fontSize: '12px',
		color: '#666',
		marginTop: '4px',
	},
	actionButton: {
		padding: '6px 12px',
		fontSize: '12px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: `1px solid ${brandPrimaryText}`,
		backgroundColor: 'white',
		color: brandPrimaryText,
		marginRight: '8px',
		':hover': {
			backgroundColor: brandPrimaryText,
			color: 'white',
		},
	},
	transcriptDialog: {
		maxWidth: '900px',
		width: '90vw',
		maxHeight: '80vh',
	},
	transcriptDialogWide: {
		maxWidth: '1100px',
		width: '1100px',
		maxHeight: '80vh',
	},
	transcriptDialogBody: {
		overflowY: 'auto',
		maxHeight: 'calc(80vh - 120px)',
	},
	transcriptCard: {
		marginBottom: '16px',
		padding: '16px',
		backgroundColor: '#f9f9f9',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
	},
	transcriptCardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: '16px',
	},
	transcriptTopic: {
		fontSize: '16px',
		fontWeight: 600,
		color: header,
		marginBottom: '8px',
	},
	transcriptMeta: {
		fontSize: '13px',
		color: '#666',
		marginBottom: '4px',
	},
	transcriptReasons: {
		fontSize: '12px',
		color: '#666',
		fontStyle: 'italic',
		marginTop: '4px',
	},
	transcriptLink: {
		background: 'none',
		border: 'none',
		color: brandPrimaryText,
		fontSize: '13px',
		fontWeight: 600,
		cursor: 'pointer',
		textDecoration: 'underline',
		padding: 0,
		':hover': {
			opacity: 0.8,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	viewTranscriptLink: {
		background: 'none',
		border: 'none',
		color: '#666',
		fontSize: '12px',
		fontWeight: 'normal',
		cursor: 'pointer',
		textDecoration: 'underline',
		padding: 0,
		':hover': {
			opacity: 0.8,
		},
	},
	transcriptCardFooter: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: '12px',
		paddingTop: '12px',
		borderTop: `1px solid ${midGray}`,
	},
	expandedContent: {
		marginTop: '16px',
		paddingTop: '16px',
		borderTop: `1px solid ${midGray}`,
	},
	summaryInCard: {
		marginTop: '16px',
		padding: '16px',
		backgroundColor: '#f0f8ff',
		borderRadius: '4px',
		border: `2px solid ${brandPrimaryText}`,
	},
	summaryTitle: {
		fontSize: '14px',
		fontWeight: 600,
		color: header,
		marginBottom: '8px',
	},
	summaryText: {
		fontSize: '14px',
		lineHeight: '1.6',
		whiteSpace: 'pre-wrap',
	},
	transcriptContent: {
		marginTop: '16px',
		padding: '16px',
		backgroundColor: 'white',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		maxHeight: '400px',
		overflowY: 'auto',
		whiteSpace: 'pre-wrap',
		fontSize: '13px',
		lineHeight: '1.6',
	},
	matchScore: {
		display: 'inline-block',
		padding: '4px 8px',
		borderRadius: '4px',
		fontSize: '12px',
		fontWeight: 600,
	},
	matchScoreHigh: {
		backgroundColor: '#d4edda',
		color: '#155724',
	},
	matchScoreMedium: {
		backgroundColor: '#fff3cd',
		color: '#856404',
	},
	matchScoreLow: {
		backgroundColor: '#f8d7da',
		color: '#721c24',
	},
	noDataMessage: {
		padding: '24px',
		textAlign: 'center',
		color: header,
		fontSize: '14px',
		maxWidth: '600px',
		margin: '0 auto',
	},
	errorMessage: {
		padding: '12px',
		backgroundColor: '#f8d7da',
		color: '#721c24',
		borderRadius: '4px',
		marginBottom: '16px',
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '32px',
	},
	userInfo: {
		padding: '12px',
		backgroundColor: '#d4edda',
		color: '#155724',
		borderRadius: '4px',
		marginBottom: '16px',
		fontSize: '14px',
	},
	notesRecordedIndicator: {
		padding: '6px 12px',
		backgroundColor: '#d4edda',
		color: '#155724',
		borderRadius: '4px',
		fontSize: '12px',
		fontWeight: 600,
		display: 'inline-block',
	},
	noShowIndicator: {
		padding: '6px 12px',
		backgroundColor: '#dc3545',
		color: 'white',
		borderRadius: '4px',
		fontSize: '12px',
		fontWeight: 600,
		display: 'inline-block',
	},
	// Meeting Note Form Styles
	meetingNoteForm: {
		marginTop: '16px',
	},
	formTitle: {
		fontSize: '16px',
		fontWeight: 600,
		color: header,
		marginBottom: '20px',
	},
	formColumns: {
		display: 'grid',
		gridTemplateColumns: '1fr 400px',
		gap: '32px',
		alignItems: 'flex-start',
	},
	formLeftColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: '20px',
	},
	formRightColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: '16px',
		backgroundColor: 'white',
		padding: '16px',
		paddingLeft: '10px',
		borderRadius: '4px',
	},
	formField: {
		marginBottom: 0,
	},
	formLabel: {
		display: 'block',
		fontSize: '14px',
		fontWeight: 600,
		color: header,
		marginBottom: '8px',
	},
	required: {
		color: '#d32f2f',
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		fontSize: '14px',
		color: header,
		cursor: 'pointer',
	},
	textarea: {
		width: '100%',
		padding: '10px',
		fontSize: '14px',
		lineHeight: '1.6',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		fontFamily: 'inherit',
		resize: 'vertical',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
	},
	select: {
		width: '100%',
		padding: '10px',
		fontSize: '14px',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		backgroundColor: 'white',
		cursor: 'pointer',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
	},
	selectedAccountDisplay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '10px',
		backgroundColor: '#d4edda',
		borderRadius: '4px',
		border: '1px solid #c3e6cb',
	},
	changeAccountButton: {
		padding: '4px 12px',
		fontSize: '12px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: `1px solid ${brandPrimaryText}`,
		backgroundColor: 'white',
		color: brandPrimaryText,
		':hover': {
			backgroundColor: brandPrimaryText,
			color: 'white',
		},
	},
	formActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '12px',
		marginTop: '16px',
	},
	cancelButton: {
		padding: '10px 24px',
		fontSize: '14px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		backgroundColor: 'white',
		color: header,
		':hover:not(:disabled)': {
			backgroundColor: '#f5f5f5',
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	saveButton: {
		padding: '10px 24px',
		fontSize: '14px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: 'none',
		backgroundColor: brandPrimaryText,
		color: 'white',
		':hover:not(:disabled)': {
			opacity: 0.9,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	validationErrors: {
		marginBottom: '16px',
		padding: '12px',
		backgroundColor: '#f8d7da',
		borderRadius: '4px',
		border: '1px solid #f5c6cb',
	},
	validationError: {
		fontSize: '14px',
		color: '#721c24',
		marginBottom: '4px',
		':last-child': {
			marginBottom: 0,
		},
	},
	nextMeetingContainer: {
		display: 'flex',
		gap: '8px',
		alignItems: 'center',
	},
	nextMeetingButton: {
		flex: 1,
		padding: '10px',
		fontSize: '14px',
		textAlign: 'left',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		backgroundColor: 'white',
		cursor: 'pointer',
		color: header,
		':hover': {
			backgroundColor: '#f5f5f5',
		},
	},
	clearDateButton: {
		padding: '8px 16px',
		fontSize: '13px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		backgroundColor: 'white',
		color: '#666',
		':hover': {
			backgroundColor: '#f5f5f5',
		},
	},
	datePickerContainer: {
		marginTop: '8px',
		padding: '16px',
		backgroundColor: 'white',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
	},
	// No Show Dialog Styles
	markNoShowButton: {
		marginLeft: '8px',
		padding: '2px 8px',
		fontSize: '11px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '3px',
		border: `1px solid #dc3545`,
		backgroundColor: 'white',
		color: '#dc3545',
		':hover': {
			backgroundColor: '#dc3545',
			color: 'white',
		},
	},
	noShowDialog: {
		maxWidth: '500px',
		width: '90vw',
	},
	noShowDialogBody: {
		padding: '20px',
	},
	noShowFormField: {
		marginBottom: '20px',
	},
	noShowFormLabel: {
		display: 'block',
		fontSize: '14px',
		fontWeight: 600,
		color: header,
		marginBottom: '8px',
	},
	noShowDialogActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '12px',
		marginTop: '24px',
		paddingTop: '20px',
		borderTop: `1px solid ${midGray}`,
	},

	// Web Team Confirmation Styles
	webTeamConfirmation: {
		padding: '32px',
		display: 'flex',
		flexDirection: 'column',
		gap: '24px',
	},
	confirmationMessage: {
		textAlign: 'center',
		padding: '24px',
		backgroundColor: background,
		borderRadius: '8px',
	},
	confirmationTitle: {
		fontSize: '20px',
		fontWeight: 600,
		color: titles,
		marginBottom: '16px',
	},
	confirmationText: {
		fontSize: '16px',
		lineHeight: '1.5',
		color: darkGrayFontColor,
	},
	confirmationActions: {
		display: 'flex',
		gap: '16px',
		justifyContent: 'center',
	},
	summaryHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: '8px',
	},
	summaryNote: {
		fontSize: '13px',
		color: darkGrayFontColor,
		fontStyle: 'italic',
		marginBottom: '8px',
	},
	summaryContent: {
		padding: '16px',
		backgroundColor: background,
		borderRadius: '8px',
		whiteSpace: 'pre-wrap',
		maxHeight: '400px',
		overflowY: 'auto',
		fontFamily: 'monospace',
		fontSize: '13px',
		lineHeight: '1.6',
		border: `1px solid ${borderOutline}`,
	},
	summaryActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: '16px',
	},

	// Two-column confirmation layout styles
	confirmationContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '24px',
		padding: '32px',
	},
	confirmationColumns: {
		display: 'flex',
		gap: '24px',
	},
	confirmationColumn: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: '16px',
		minWidth: 0,
	},
	confirmationColumnFull: {
		maxWidth: '600px',
		margin: '0 auto',
	},
	confirmationPanel: {
		padding: '20px',
		backgroundColor: background,
		borderRadius: '8px',
		border: `1px solid ${borderOutline}`,
	},
	confirmationPanelTitle: {
		fontSize: '16px',
		fontWeight: 600,
		color: titles,
		marginBottom: '16px',
		marginTop: 0,
	},

	// Deep Research input fields for web team
	deepResearchFields: {
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		marginBottom: '16px',
	},
	deepResearchFieldLabel: {
		display: 'block',
		fontSize: '13px',
		fontWeight: 600,
		color: darkGrayFontColor,
		marginBottom: '4px',
	},
	deepResearchFieldInput: {
		width: '100%',
		padding: '8px 12px',
		fontSize: '14px',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		boxSizing: 'border-box',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: '#f5f5f5',
			cursor: 'not-allowed',
		},
	},

	// Utility styles to replace inline styles
	loadingText: {
		marginLeft: '16px',
	},
	onboardingText: {
		marginLeft: '8px',
	},
	transcriptDivider: {
		margin: '0 8px',
	},
	fullWidth: {
		flex: 1,
	},
	transcriptPreview: {
		marginBottom: '16px',
		padding: '12px',
		backgroundColor: background,
		borderRadius: '4px',
	},
	transcriptPreviewTitle: {
		fontWeight: 600,
		marginBottom: '4px',
	},
	transcriptPreviewContent: {
		fontSize: '13px',
		color: darkGrayFontColor,
	},
	// Transcript loading status banners
	warningBanner: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		padding: '12px 16px',
		backgroundColor: warningLight,
		color: warningDark,
		borderRadius: '4px',
		marginBottom: '16px',
		fontSize: '14px',
		border: `1px solid ${warningDark}`,
	},
	warningIcon: {
		fontSize: '16px',
		flexShrink: 0,
	},
	errorBanner: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '16px',
		padding: '24px',
		backgroundColor: errorLight,
		color: destructive,
		borderRadius: '4px',
		textAlign: 'center',
		border: `1px solid ${destructive}`,
	},
	errorIcon: {
		fontSize: '24px',
	},
	errorText: {
		fontSize: '14px',
		fontWeight: 500,
	},
	retryButton: {
		padding: '8px 20px',
		fontSize: '14px',
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: '4px',
		border: `1px solid ${destructive}`,
		backgroundColor: white,
		color: destructive,
		':hover:not(:disabled)': {
			backgroundColor: destructive,
			color: white,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
});
